/**
 * Rupee amounts spelled out, Indian numbering system.
 *
 * DERIVED, NEVER STORED. The credit note and invoice both print "Rupees … Only"
 * beside the figure they describe. Storing that sentence would let it drift from
 * the number — a financial document whose words and digits disagree is worse
 * than one with neither, and which of the two is authoritative becomes a real
 * dispute. So it is computed from the amount every time it is rendered.
 *
 * Indian grouping, not Western: 1,00,000 is "One Lakh", 1,00,00,000 is "One
 * Crore". `Intl.NumberFormat('en-IN')` gets the digit grouping right but has no
 * words, and the English short scale ("hundred thousand", "million") is wrong
 * for a document printed in India.
 */
const ONES = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
];
const TENS = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety',
];
/** 0–99. */
function underHundred(n) {
    if (n < 20)
        return ONES[n] ?? '';
    const tens = TENS[Math.floor(n / 10)] ?? '';
    const ones = ONES[n % 10] ?? '';
    return ones === '' ? tens : `${tens} ${ones}`;
}
/** 0–999. */
function underThousand(n) {
    const hundreds = Math.floor(n / 100);
    const rest = n % 100;
    const head = hundreds > 0 ? `${ONES[hundreds] ?? ''} Hundred` : '';
    const tail = underHundred(rest);
    if (head === '')
        return tail;
    return tail === '' ? head : `${head} ${tail}`;
}
/**
 * Whole rupees to words, no "Rupees"/"Only" wrapper.
 *
 * Handles up to 99,99,99,99,999 (arab). Beyond that it falls back to the grouped
 * numeral rather than inventing scale names nobody prints.
 */
export function numberToWordsIndian(value) {
    const n = Math.floor(Math.abs(value));
    if (n === 0)
        return 'Zero';
    if (n > 99_99_99_99_999)
        return new Intl.NumberFormat('en-IN').format(n);
    // Indian scale: last 3 digits, then groups of 2.
    const parts = [];
    const scales = [
        { divisor: 1_00_00_00_000, name: 'Arab' },
        { divisor: 1_00_00_000, name: 'Crore' },
        { divisor: 1_00_000, name: 'Lakh' },
        { divisor: 1_000, name: 'Thousand' },
    ];
    let remaining = n;
    for (const { divisor, name } of scales) {
        const count = Math.floor(remaining / divisor);
        if (count > 0) {
            parts.push(`${underThousand(count)} ${name}`);
            remaining %= divisor;
        }
    }
    if (remaining > 0)
        parts.push(underThousand(remaining));
    return parts.join(' ').replace(/\s+/g, ' ').trim();
}
/**
 * The full sentence a document prints, e.g.
 * "Rupees Two Lakh Twenty Four Thousand Only" or
 * "Rupees One Thousand and Fifty Paise Only".
 *
 * Paise are included only when non-zero, matching how Indian invoices are
 * conventionally written.
 */
export function amountToWordsInr(amount) {
    const negative = amount < 0;
    const absolute = Math.abs(amount);
    const rupees = Math.floor(absolute);
    // Rounded, not truncated: 0.005 must not silently vanish from a total.
    const paise = Math.round((absolute - rupees) * 100);
    // Rounding can carry: 99.999 -> 100 rupees, 0 paise.
    const carriedRupees = paise === 100 ? rupees + 1 : rupees;
    const carriedPaise = paise === 100 ? 0 : paise;
    const words = carriedPaise > 0
        ? `${numberToWordsIndian(carriedRupees)} and ${numberToWordsIndian(carriedPaise)} Paise`
        : numberToWordsIndian(carriedRupees);
    return `Rupees ${negative ? 'Minus ' : ''}${words} Only`;
}
//# sourceMappingURL=amount-in-words.js.map